import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '@/components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { FCRAFooterText } from '@/components/layout/FCRADisclaimer';
import { submitContactForm } from '@/services/contactAPI';
import { logger, ValidationError, NetworkError } from '@/utils/logger';

export const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState<string>('');

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Contact Us - CollectABILITY';

    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    updateMetaTag('description', 'Get in touch with the CollectABILITY team. We\'re here to help with your debt collection intelligence needs.');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    try {
      // Submit form to Zendesk via Lambda
      const response = await submitContactForm({
        name: formData.name,
        email: formData.email,
        company: formData.company,
        message: formData.message,
      });

      if (response.success) {
        logger.info('Contact form submitted successfully. Ticket ID:', response.ticketId);
        setSubmittedEmail(formData.email);
        setSubmitSuccess(true);
        setFormData({ name: '', email: '', company: '', message: '' });
      } else {
        const errorMsg = response.error || 'Failed to submit contact form';
        logger.error('Contact form submission failed:', errorMsg);
        setSubmitError(errorMsg);
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        const errorMsg = `Validation error: ${error.message}`;
        logger.error('Contact form validation error:', error);
        setSubmitError(errorMsg);
      } else if (error instanceof NetworkError) {
        const errorMsg = `Network error: ${error.message}. Please check your connection and try again.`;
        logger.error('Contact form network error:', error);
        setSubmitError(errorMsg);
      } else {
        const errorMsg = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again later.';
        logger.error('Contact form unexpected error:', error);
        setSubmitError(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="pt-16 pb-12 lg:pt-20 lg:pb-16 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Have questions about our products or need support? We're here to help. Get in touch with our team.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-10 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-2"
            >
              <Card className="">
                <CardHeader>
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    {submitSuccess ? 'Message Sent!' : 'Send us a Message'}
                  </CardTitle>
                  <p className="text-slate-600 mt-2">
                    {submitSuccess
                      ? 'Thank you for contacting us. We\'ll get back to you soon.'
                      : 'Fill out the form below and we\'ll respond within 24 hours.'}
                  </p>
                </CardHeader>
                <CardContent>
                  {submitSuccess ? (
                    <div className="space-y-6">
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.5, type: 'spring' }}
                        className="flex justify-center"
                      >
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                          <svg
                            className="w-10 h-10 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                      </motion.div>

                      <div className="text-center space-y-4">
                        <p className="text-lg text-slate-700">
                          Your message has been successfully submitted to our support team.
                        </p>
                        <p className="text-sm text-slate-600">
                          We typically respond within 24 hours during business days (Monday-Friday, 9am-5pm MT).
                        </p>
                        <p className="text-sm text-slate-600">
                          You'll receive a response at <span className="font-semibold">{submittedEmail}</span>.
                        </p>
                      </div>

                      <div className="pt-4">
                        <Button
                          onClick={() => {
                            setSubmitSuccess(false);
                            setSubmittedEmail('');
                            setSubmitError(null);
                            setFormData({ name: '', email: '', company: '', message: '' });
                          }}
                          className="w-full ca-btn ca-btn--secondary"
                        >
                          Send Another Message
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Name <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@company.com"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name
                      </label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Company"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                        Message <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us how we can help..."
                        rows={6}
                        className="w-full"
                      />
                    </div>

                    {/* Error Message */}
                    {submitError && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-red-50 border border-red-200 rounded-lg"
                      >
                        <p className="text-red-800 text-sm">
                          {submitError}
                        </p>
                      </motion.div>
                    )}

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full ca-btn ca-btn--primary text-white"
                    >
                      {isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-3"
            >
              <Card className="">
                <CardHeader>
                  <CardTitle className="text-xl font-bold text-slate-900">
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-collectability-blue rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a
                        href="mailto:support@collectability.ai"
                        className="text-collectability-blue hover:text-collectability-blue-dark text-sm transition-colors"
                      >
                        support@collectability.ai
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-collectability-blue rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                      <a
                        href="tel:+12089251521"
                        className="text-collectability-blue hover:text-collectability-blue-dark text-sm transition-colors"
                      >
                        (208) 925-1521
                      </a>
                      <p className="text-xs text-slate-600 mt-1">Mon-Fri, 9am-5pm MT</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-collectability-blue rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Address</h3>
                      <p className="text-sm text-slate-600">
                        2976 E State St<br />
                        Suite 120-2914<br />
                        Eagle, ID 83616<br />
                        United States
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className=" bg-collectability-blue text-white">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-lg mb-2">Need Immediate Help?</h3>
                  <p className="text-sm text-white/90 mb-4">
                    Check out our documentation or start using our products right away.
                  </p>
                  <div className="space-y-2">
                    <Link
                      to="/batch"
                      className="block w-full bg-white text-collectability-blue px-4 py-2 rounded-lg text-sm font-semibold text-center hover:bg-slate-100 transition-colors"
                    >
                      Batch Processing
                    </Link>
                    <Link
                      to="/search"
                      className="block w-full bg-white text-collectability-blue px-4 py-2 rounded-lg text-sm font-semibold text-center hover:bg-slate-100 transition-colors"
                    >
                      Web Search
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img
                  src="/linear-logo-sm.png"
                  alt="CollectABILITY"
                  className="h-10"
                  loading="lazy"
                  height="40"
                />
              </Link>
              <p className="text-slate-700 text-sm mb-4 max-w-md">
                AI-powered debt collection intelligence platform for smarter collections and higher returns.
              </p>
            </div>

            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Product</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/batch" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Batch Processing
                  </Link>
                </li>
                <li>
                  <Link to="/search" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Web Search
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-slate-900 font-semibold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/contact" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Contact Us
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Terms of Use
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-slate-700 hover:text-slate-900 text-sm transition-colors">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-300 pt-8 space-y-3">
            <FCRAFooterText />
            <p className="text-slate-700 text-sm text-center">
              &copy; 2024 CollectABILITY, LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

